import { useState } from "react";
import { Plus, Trash2, ChevronDown, ChevronUp, Globe } from "lucide-react";
import type { SiteConfig, FAQItem } from "./AdminDataContext";

const LANGS = [
  { code: "en" as const, label: "English", short: "EN", required: true },
  { code: "ja" as const, label: "日本語", short: "JP", required: false },
  { code: "nl" as const, label: "Nederlands", short: "NL", required: false },
  { code: "fr" as const, label: "Français", short: "FR", required: false },
];

function emptyFaqItem(): FAQItem {
  return {
    id: `faq_${Date.now()}`,
    q: { en: "" },
    a: { en: "" },
  };
}

export function AdminFAQTab({
  config,
  setConfig,
}: {
  config: SiteConfig;
  setConfig: (c: SiteConfig) => void;
}) {
  const items = config.faqItems || [];
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [activeLang, setActiveLang] = useState<"en" | "ja" | "nl" | "fr">("en");

  const setItems = (newItems: FAQItem[]) => {
    setConfig({ ...config, faqItems: newItems });
  };

  const addItem = () => {
    const item = emptyFaqItem();
    setItems([...items, item]);
    setExpandedId(item.id);
    setActiveLang("en");
  };

  const deleteItem = (id: string) => {
    setItems(items.filter((i) => i.id !== id));
    if (expandedId === id) setExpandedId(null);
  };

  const moveItem = (idx: number, dir: -1 | 1) => {
    const newItems = [...items];
    const target = idx + dir;
    if (target < 0 || target >= newItems.length) return;
    [newItems[idx], newItems[target]] = [newItems[target], newItems[idx]];
    setItems(newItems);
  };

  const updateField = (id: string, field: "q" | "a", lang: string, value: string) => {
    setItems(
      items.map((item) =>
        item.id === id
          ? { ...item, [field]: { ...item[field], [lang]: value } }
          : item
      )
    );
  };

  const filledCount = (item: FAQItem, lang: string) => {
    if (lang === "en") return (item.q.en ? 1 : 0) + (item.a.en ? 1 : 0);
    const q = item.q[lang as keyof typeof item.q];
    const a = item.a[lang as keyof typeof item.a];
    return (q ? 1 : 0) + (a ? 1 : 0);
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl p-6 shadow-[0_2px_16px_rgba(40,32,20,0.04)]">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2
              className="text-[#2D2A26] tracking-[0.08em]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "20px", fontWeight: 400 }}
            >
              Matcha Guide & FAQ
            </h2>
            <p className="text-[#6F675F] mt-1" style={{ fontSize: "13px", fontWeight: 300 }}>
              Manage questions and answers shown on the homepage. Supports all 4 languages.
            </p>
          </div>
          <button
            onClick={addItem}
            className="flex items-center gap-2 px-4 py-2 bg-[#6F7F63] text-white rounded-lg hover:bg-[#5F6F53] transition-colors tracking-[0.1em] uppercase shrink-0"
            style={{ fontSize: "11px" }}
          >
            <Plus size={14} /> Add Question
          </button>
        </div>

        {items.length === 0 ? (
          <div className="text-center py-12 text-[#6F675F]" style={{ fontSize: "13px", fontWeight: 300 }}>
            No FAQ items yet. Click "Add Question" to create one.
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((item, idx) => (
              <div
                key={item.id}
                className="border border-[#E6E0D7] rounded-xl overflow-hidden"
              >
                {/* Collapsed header */}
                <div className="flex items-center gap-3 px-4 py-3 bg-[#F7F5F1]">
                  <div className="flex flex-col gap-0.5">
                    <button
                      onClick={() => moveItem(idx, -1)}
                      disabled={idx === 0}
                      className="text-[#D8D1C7] hover:text-[#6F675F] disabled:opacity-30 transition-colors"
                    >
                      <ChevronUp size={12} />
                    </button>
                    <button
                      onClick={() => moveItem(idx, 1)}
                      disabled={idx === items.length - 1}
                      className="text-[#D8D1C7] hover:text-[#6F675F] disabled:opacity-30 transition-colors"
                    >
                      <ChevronDown size={12} />
                    </button>
                  </div>
                  <button
                    onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                    className="flex-1 text-left min-w-0"
                  >
                    <p
                      className="text-[#2D2A26] truncate"
                      style={{ fontSize: "13px" }}
                    >
                      {item.q.en || <span className="text-[#D8D1C7] italic">Untitled question</span>}
                    </p>
                    <div className="flex gap-1.5 mt-1">
                      {LANGS.map((l) => (
                        <span
                          key={l.code}
                          className={`px-1.5 py-0.5 rounded ${
                            filledCount(item, l.code) === 2
                              ? "bg-[#6F7F63]/15 text-[#6F7F63]"
                              : filledCount(item, l.code) > 0
                              ? "bg-[#C7A76C]/15 text-[#C7A76C]"
                              : "bg-[#E6E0D7]/50 text-[#D8D1C7]"
                          }`}
                          style={{ fontSize: "9px", letterSpacing: "0.05em" }}
                        >
                          {l.short}
                        </span>
                      ))}
                    </div>
                  </button>
                  <button
                    onClick={() => deleteItem(item.id)}
                    className="text-[#D8D1C7] hover:text-[#8B5C58] transition-colors shrink-0 p-1"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>

                {/* Expanded editor */}
                {expandedId === item.id && (
                  <div className="px-4 pb-4 pt-2">
                    {/* Language tabs */}
                    <div className="flex items-center gap-1 mb-4">
                      <Globe size={13} className="text-[#6F675F] mr-1" />
                      {LANGS.map((l) => (
                        <button
                          key={l.code}
                          onClick={() => setActiveLang(l.code)}
                          className={`px-3 py-1.5 rounded-lg tracking-[0.08em] transition-all duration-200 ${
                            activeLang === l.code
                              ? "bg-[#6F7F63] text-white"
                              : "text-[#6F675F] hover:bg-[#F2EEE8]"
                          }`}
                          style={{ fontSize: "11px" }}
                        >
                          {l.short}
                          {l.required && <span className="text-[#C7A76C] ml-0.5">*</span>}
                        </button>
                      ))}
                    </div>

                    <div className="space-y-3">
                      <div>
                        <label
                          className="block text-[#6F675F] tracking-[0.12em] uppercase mb-1.5"
                          style={{ fontSize: "10px" }}
                        >
                          Question {activeLang !== "en" && "(leave empty to use English)"}
                        </label>
                        <input
                          type="text"
                          value={(item.q[activeLang as keyof typeof item.q] as string) || ""}
                          onChange={(e) => updateField(item.id, "q", activeLang, e.target.value)}
                          placeholder={activeLang !== "en" ? item.q.en : "Enter question..."}
                          maxLength={500}
                          className="w-full px-3 py-2.5 bg-[#F7F5F1] border border-[#E6E0D7] rounded-lg text-[#2D2A26] placeholder:text-[#D8D1C7]/60 focus:outline-none focus:border-[#C7A76C]"
                          style={{ fontSize: "13px", fontFamily: "'Jost', sans-serif" }}
                        />
                      </div>
                      <div>
                        <label
                          className="block text-[#6F675F] tracking-[0.12em] uppercase mb-1.5"
                          style={{ fontSize: "10px" }}
                        >
                          Answer {activeLang !== "en" && "(leave empty to use English)"}
                        </label>
                        <textarea
                          rows={4}
                          value={(item.a[activeLang as keyof typeof item.a] as string) || ""}
                          onChange={(e) => updateField(item.id, "a", activeLang, e.target.value)}
                          placeholder={activeLang !== "en" ? item.a.en : "Enter answer..."}
                          maxLength={2000}
                          className="w-full px-3 py-2.5 bg-[#F7F5F1] border border-[#E6E0D7] rounded-lg text-[#2D2A26] placeholder:text-[#D8D1C7]/60 focus:outline-none focus:border-[#C7A76C] resize-none"
                          style={{ fontSize: "13px", fontFamily: "'Jost', sans-serif" }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
